import { getFormatAmount } from "@/Common/helpers/number";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import { DATE_FORMAT } from "@/Constants/date";
import { CELL_CLASS_ERROR, CELL_FONT_SIZE, FOOTER_CELL_FONT_SIZE, TABLE_ROW_HEIGHT } from "@/Constants/tables";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import PropTypes from "prop-types";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

const paintRedIfInvalid = (params, validationField) =>
	getCellClassNameFn(
		[],
		{ isNegative: false, isFooter: isFooterCell(params) },
		{ base: !params.row?.[validationField] ? CELL_CLASS_ERROR : "" }
	)();

export default function SaleInvoicesTable({ columnProps = {}, data = [] }) {
	const aggregations = useAggregationExtended({ uniqueCount: true });
	const apiRef = useGridApiRef();
	const columns = [
		{ field: "_id", headerName: "ID", width: 10, hide: true },
		{
			field: "invoiceDate",
			headerName: "Invoice Date",
			type: "date",
			...columnProps,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return (
						<CustomCell value="Totals" fontSize={FOOTER_CELL_FONT_SIZE} fontFamily="font-montserrat " classes="font-medium" />
					);
				}
				return (
					<CustomCell
						value={params.formattedValue}
						hasTooltip
						maxWidth={params.colDef.computedWidth || params.colDef.width}
						fontSize={CELL_FONT_SIZE}
					/>
				);
			},
		},
		{ field: "accountNumber", headerName: "Acct #", ...columnProps },
		{ field: "referenceNumber", headerName: "Ref #", ...columnProps },
		{
			field: "invoiceType",
			headerName: "Chargeback Type",
			...columnProps,
			cellClassName: (params) => paintRedIfInvalid(params, "isValidCode"),
		},
		{
			field: "addedBy",
			headerName: "User ID",
			...columnProps,
			cellClassName: (params) => paintRedIfInvalid(params, "isValidEmployee"),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return (
						<CustomCell
							value={`Users: ${params.value}`}
							hasTooltip
							maxWidth={params.colDef.width}
							fontSize={FOOTER_CELL_FONT_SIZE}
							fontFamily="font-montserrat"
							classes="font-medium"
						/>
					);
				}
				return <CustomCell value={params.value} hasTooltip maxWidth={params.colDef.width} fontSize={CELL_FONT_SIZE} />;
			},
		},
		{
			field: "employeeName",
			headerName: "Employee",
			...columnProps,
			cellClassName: (params) => paintRedIfInvalid(params, "isValidEmployee"),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return (
						<CustomCell
							value={`Employees: ${params.value}`}
							hasTooltip
							maxWidth={params.colDef.width}
							fontSize={FOOTER_CELL_FONT_SIZE}
							fontFamily="font-montserrat"
							classes="font-medium"
						/>
					);
				}
				return <CustomCell value={params.value} hasTooltip maxWidth={params.colDef.width} fontSize={CELL_FONT_SIZE} />;
			},
		},
		{ field: "controlNumber", headerName: "Ctrl #", ...columnProps },
		{
			field: "invoiceAmount",
			headerName: "Invoice Amount",
			...columnProps,
			align: "right",
			type: "number",
			cellClassName: (params) => {
				if (isFooterCell(params)) {
					return "footer-amount-no-bg";
				}
				return getCellClassNameFn([], { isFunded: false }, {})(params);
			},
			renderCell: (params) => {
				const isFooter = isFooterCell(params);
				return (
					<TypographyCell
						render="absolute"
						params={params}
						hints={{ isMoney: true, noFocus: true }}
						classes={isFooter ? "footer-label-no-bg footer-amount-no-bg" : undefined}
						fontSize={isFooter ? FOOTER_CELL_FONT_SIZE : CELL_FONT_SIZE}
					/>
				);
			},
		},
		{
			field: "units",
			headerName: "Units",
			align: "right",
			...columnProps,
			maxWidth: 80,
			valueFormatter: (value) => value && getFormatAmount(value, 1),
			renderCell: (params) => (
				<CustomCell
					value={params.formattedValue}
					hasTooltip
					maxWidth={params.colDef.width}
					fontSize={CELL_FONT_SIZE}
					classes="pr-2"
				/>
			),
		},
	];

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={columns}
			rows={data}
			initialState={{
				aggregation: {
					model: {
						invoiceAmount: "sum",
						addedBy: "uniqueCount",
						employeeName: "uniqueCount",
					},
				},
			}}
			rowHeight={TABLE_ROW_HEIGHT}
			hideFooter
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{ _id: false }}
			slots={{ noRowsOverlay: NoRowsOverlay }}
			getRowClassName={(params) => (isFooterCell(params) ? "aggregation-footer gray-footer" : "")}
			customProps={{
				tableClassName: { "functional-table": true },
				miniSize: true,
				hide: false,
				tableContainerStyle: { height: "auto", width: "100%", overflow: "hidden" },
				enableRowExpansion: false,
				customAggregationsFn: { ...aggregations },
				aggregationFooterClassName: "aggregation-footer gray-footer",
				disableFooterPrimaryBg: true,
			}}
		/>
	);
}

SaleInvoicesTable.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object),
	columnProps: PropTypes.object,
};
